import React, { useEffect, useState } from "react";
import { ArrowRight, ChevronDown } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/19687774/pexels-photo-19687774.jpeg?auto=compress&cs=tinysrgb&w=2000";

const Hero = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  // Subtle parallax: image moves slower than scroll
  const parallaxY = scrollY * 0.35;
  const overlayOpacity = Math.min(1, 1 + scrollY * 0.0008);

  return (
    <section
      id="inicio"
      data-testid="section-hero"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      {/* Background image with parallax */}
      <div className="absolute inset-0 will-change-transform">
        <div
          className="absolute inset-0 -top-10 -bottom-20"
          style={{ transform: `translate3d(0, ${parallaxY}px, 0) scale(1.05)` }}
        >
          <img
            src={HERO_IMG}
            alt="Marina profesional con embarcaciones amarradas"
            className="w-full h-full object-cover"
          />
        </div>
        <div
          className="absolute inset-0 bg-gradient-to-r from-navy-900/85 via-navy-800/75 to-navy-700/55"
          style={{ opacity: overlayOpacity }}
        />
        <div className="absolute inset-0 grain opacity-40" />

        {/* Premium light beam */}
        <div className="absolute -top-1/3 -right-1/4 w-[60%] h-[140%] bg-gradient-to-bl from-sky-300/10 via-transparent to-transparent blur-3xl pointer-events-none" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-32 sm:py-40">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-md fade-up">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-sky-300 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sky-400" />
            </span>
            <span className="text-xs uppercase tracking-[0.22em] text-white/85 font-medium">
              Derecho marítimo y portuario
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium text-white leading-[1.05] tracking-tight mt-6 fade-up fade-up-delay-1"
          >
            Gestión legal y técnica de embarcaciones{" "}
            <span className="italic text-sky-300">abandonadas</span> o con deuda en puertos y marinas
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-7 text-base sm:text-lg text-white/85 leading-relaxed max-w-2xl fade-up fade-up-delay-2"
          >
            PORT&rsquo;S COLLECTORS acompaña a puertos, marinas y clubes náuticos en la recuperación
            de amarres ocupados por embarcaciones impagadas, abandonadas o pendientes de resolución
            judicial.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 fade-up fade-up-delay-3">
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollTo("contacto")}
              className="group relative inline-flex items-center justify-center gap-2 bg-white text-navy hover:text-navy-900 px-8 py-4 rounded-md font-semibold text-sm tracking-wide transition-all duration-500 overflow-hidden shadow-xl shadow-navy-900/30 hover:shadow-2xl hover:shadow-sky-300/40"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-sky-300 to-white translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500 ease-out" />
              <span className="relative z-10">Contactar</span>
              <ArrowRight
                size={16}
                className="relative z-10 transition-transform group-hover:translate-x-1"
              />
            </button>
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollTo("servicios")}
              className="group inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white/10 hover:border-white px-8 py-4 rounded-md font-medium text-sm tracking-wide transition-all duration-300"
            >
              Ver servicios
              <span className="w-0 group-hover:w-4 h-px bg-white transition-all duration-300" />
            </button>
          </div>
        </div>
      </div>

      <button
        onClick={() => scrollTo("servicios")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors group"
        data-testid="hero-scroll-indicator"
        aria-label="Desplazar abajo"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.3em] opacity-0 group-hover:opacity-100 transition-opacity">
            Descubrir
          </span>
          <ChevronDown size={26} strokeWidth={1.5} className="animate-bounce" />
        </div>
      </button>
    </section>
  );
};

export default Hero;
